import React, { useState } from 'react';
import styled from 'styled-components/macro';

import { ReactComponent as Globe } from 'icons/icon-globe.svg';
import { ReactComponent as European } from 'icons/icon-European.svg';
import { ReactComponent as Fallback } from 'icons/icon-flag-placeholder.svg';
import { ReactComponent as Other } from 'icons/icon-flag-other.svg';
import theme from 'styles/themes/variables';
import { ThemeProps } from 'types/styled';

const { spacing } = theme;

export const FlagImage = styled.img<ThemeProps<'img'>>`
  display: inline-block;
  margin-right: ${({ small }): string =>
    small ? spacing.xsmall : spacing.small};
  max-width: none;
`;

export const StyledGlobe = styled(Globe)`
  color: ${({ theme }): string => theme.CasperGrey};
  g {
    color: ${({ theme }): string => theme.CasperGrey};
    fill: none;
  }
`;

interface ICountryFlagProps {
  // The ISO Alpha 2 country ID
  id: string; // CountryIds | 'WW' | 'other' | 'EX-other';
  // Should the flag be large (30px wide) or small (16px wide)?
  small?: boolean;
  // The country display name
  displayName?: string;
  // @1x flag image URL
  flagImgSmall: string;
  // @2x flag image URL
  flagImgMedium: string;
  // @3x flag image URL
  flagImgLarge: string;
  // Needed by styled-components composing this component
  className?: string;
}

/*
 * Renders an image of a country flag.
 * This component should be fed 1 object from the /countries endpoint as its props.
] */
const CountryFlag: React.FC<ICountryFlagProps> = (props) => {
  const {
    id,
    displayName = 'CountryFlag',
    flagImgSmall,
    flagImgMedium,
    flagImgLarge,
    small = false,
    className,
  } = props;
  const [showFallback, setShowFallback] = useState(false);

  const withoutFlag =
    !flagImgLarge && !flagImgMedium && !flagImgSmall && id !== 'WW';

  if (id === 'other' || id === 'EX-other' || withoutFlag) {
    return (
      <FlagImage
        as={Other}
        className={className}
        width={small ? 16 : 30}
        height={small ? 11 : 20}
      />
    );
  }

  if (id === 'EU') {
    return (
      <FlagImage
        as={European}
        className={className}
        width={small ? 16 : 30}
        height={small ? 11 : 20}
      />
    );
  }

  if (id === 'WW') {
    return (
      <FlagImage
        as={StyledGlobe}
        className={className}
        width={small ? 20 : 30}
        height={small ? 15 : 20}
      />
    );
  }

  if (showFallback) {
    return (
      <FlagImage
        as={Fallback}
        className={className}
        width={small ? 16 : 30}
        height={small ? 11 : 20}
        alt={`Fallback for flag of ${displayName}`}
        data-testid={`flag-fallback-${id}`}
      />
    );
  }

  function handleError(): void {
    setShowFallback(true);
  }

  /**
   * Create the sourceset by filtering out undefined image sources and
   * adding the @.x directive
   */
  const srcSet = [flagImgSmall, flagImgMedium, flagImgLarge]
    .filter(Boolean)
    .map((src, i) => `${src} ${i + 1}x`)
    .join(', ');

  return (
    <FlagImage
      onError={handleError}
      className={className}
      src={flagImgSmall}
      srcSet={srcSet}
      width={small ? 16 : 30}
      height={small ? 11 : 20}
      alt={`Flag of ${displayName}`}
      small={small}
      data-testid={`flag-${id}`}
      decoding="async"
      importance="low"
    />
  );
};

export default React.memo(CountryFlag);
